import GlobalBlackFridayBanner from '@/shared/blackFriday/GlobalBlackFridayBanner';
import ToastMessage from '@/shared/global/ToastMessage';
import { Frame, Layout, SkeletonBodyText, SkeletonDisplayText, SkeletonPage, Card } from '@shopify/polaris';
import { PATHS } from 'config/constants';
import { useShopInfo } from 'hooks/useShopInfo';
import { useToast } from 'hooks/useToast';
import { useUpgradeModalShow } from 'hooks/useUpgradeModalShow';
import React, { Suspense, lazy, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { dismissToast } from 'states/actions/toast';

import { checkIsStoreDashboardNew } from 'utils/checkIsStoreNew';
import { perfMark } from 'utils/perfTrace';

const Home = lazy(() => import('./pages/dashboard/Dashboard'));

const PricingV2 = lazy(() => import('./pages/pricingV2/Pricing'));

// Statically importing UpgradeModal here dragged the whole pricingV2 tree
// (Plans, PlanCard + their SCSS) into the main bundle, defeating the lazy
// split on the Pricing route. It only renders when upgradeModalShow flips,
// so a lazy chunk (with its own null-fallback Suspense below, to avoid
// tripping the page-level route fallback) costs nothing visible.
const UpgradeModal = lazy(() => import('@/shared/UpgradeModal'));

// Instrumented (unlike the other lazy() routes above/below) because this is
// the chunk behind the dashboard's slow LCP — the import() call only fires
// once Router itself mounts (i.e. after App.js's shopInfo/permissions gate
// clears), and nothing previously logged how long the chunk itself took to
// download + parse vs. everything else in the [PageLoad][PERF] trail.
// The import() starts at module-evaluation time (i.e. while the main bundle
// is still executing, in parallel with app-bridge.js and the bootstrap API
// call) instead of waiting for Router to mount behind App.js's
// shopInfo/permissions gate — a measured ~1s+ of pure serialization on the
// dashboard's LCP path. lazy() then resolves instantly from this same
// promise. Pre-2021 shops (old dashboard) pay one wasted chunk download;
// they're the small minority and it's off their critical path anyway.
perfMark('HomeNew (dashboardV2) chunk import start');
const homeNewChunk = import('./pages/dashboardV2/Dashboard').then((mod) => {
  perfMark('HomeNew (dashboardV2) chunk import resolved');
  return mod;
});
const HomeNew = lazy(() => homeNewChunk);

const Help = lazy(() => import('./pages/help/Help'));
// The campaigns list is the app's most-opened page after the dashboard, and its
// chunk (table, filters, stats) is what stands between a cold load of /campaigns
// and first paint. Two ways it used to be late:
//  - Direct load (Shopify admin sidebar reboots the iframe on /campaigns): the
//    import() only fired once Router mounted, i.e. after the shopInfo/permissions
//    gate cleared. Start it at module-evaluation time instead, in parallel with
//    that gate, exactly like homeNewChunk above.
//  - In-app navigation from the dashboard: webpackPrefetch downloads the chunk at
//    idle priority right after the main bundle, so the click finds it cached.
// Every other landing path pays nothing extra beyond the idle prefetch.
const loadCampaigns = () =>
  import(/* webpackChunkName: "campaigns-page", webpackPrefetch: true */ './pages/campaigns/Campaigns');
const isCampaignsLanding = window.location.pathname.replace(/\/+$/, '') === PATHS.CAMPAIGNS;
const campaignsChunk = isCampaignsLanding ? loadCampaigns() : null;
const Campaigns = lazy(() => campaignsChunk || loadCampaigns());
const CampaignOverlaps = lazy(() => import('./pages/campaigns/Overlaps'));
const CheckProduct = lazy(() => import('./pages/campaigns/CheckProduct'));
const Setting = lazy(() => import('./pages/setting/Setting'));
const Widgets = lazy(() => import('./pages/widgetsV2/index'));
const CustomizeCountdown = lazy(() => import('./pages/countdown-settings/CustomizeCountdown'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Login'));
const ModifyCampaignV4 = lazy(() => import('./pages/campaignV4/Campaign'));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
const SafeUninstall = lazy(() => import('./pages/safeUninstall/SafeUninstall'));
const WholesaleTools = lazy(() => import('./pages/wholesaleTools/WholesaleTools'));

// Route-chunk loading fallback. An empty <div> collapses to zero height while
// the lazy-loaded page chunk streams in, then the real page pops in at full
// height all at once — a large layout shift on every first navigation to a
// route. A SkeletonPage reserves roughly the shape/height of a real page so
// that pop-in doesn't move anything.
const RouteLoadingFallback = () => (
  <SkeletonPage primaryAction fullWidth>
    <Layout>
      <Layout.Section>
        <Card>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={3} />
        </Card>
      </Layout.Section>
      <Layout.Section>
        <Card>
          <SkeletonBodyText lines={6} />
        </Card>
      </Layout.Section>
    </Layout>
  </SkeletonPage>
);

const Router = () => {
  const dispatch = useDispatch();
  const upgradeModalShow = useUpgradeModalShow();

  const shopInfo = useShopInfo();
  // checkIsStoreDashboardNew({date}) returns true/false once `created_at` is a
  // real date, but `undefined` (falsy) while shopInfo is still loading — an
  // `undefined` result used to fall through to `false` here, briefly mounting
  // the OLD dashboard (`pages/dashboard/Dashboard.js`, with its own separate
  // mount-time fetches) on the very first render after the shopInfo/permissions
  // gate clears, before a later render switched to the real one once
  // created_at landed. Measured in a real [PageLoad][PERF] trace: ~1.1s
  // between the gate clearing and the NEW dashboard's chunk import even
  // starting, plus a wasted getDasboardReport call from the old page — all of
  // it pure waste from briefly rendering the wrong dashboard. `!== false`
  // means "new" is the default while we don't know yet, and only an explicit,
  // resolved `false` (a genuinely pre-2021-02-09 shop) falls back to the old one.
  const isDashboardNew = checkIsStoreDashboardNew({ date: shopInfo?.data?.created_at }) !== false;

  const DashboardElement = isDashboardNew ? <HomeNew /> : <Home />;

  const toast = useToast();
  const handleDismissToast = useCallback(() => {
    dismissToast(dispatch);
  }, [dispatch]);

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Frame>
        {/* <TopNav /> */}

        <GlobalBlackFridayBanner />

        <Routes>
          <Route path={PATHS.PRICING} element={<PricingV2 />} />

          <Route path={PATHS.HOME} element={DashboardElement} />

          <Route path={`${PATHS.HELP}`} element={<Help />} />
          <Route path={`${PATHS.HELP}/:tab`} element={<Help />} />
          <Route path={PATHS.SETTING} element={<Setting />} />
          {/* "/settings" is a natural guess (and an old link shape) but the route is
              "/setting"; without this it rendered a blank page. */}
          <Route path={`${PATHS.SETTING}s`} element={<Navigate to={`${PATHS.SETTING}${window.location.search}`} replace />} />
          <Route path={`${PATHS.COUNTDOWN_SETTINGS}`} element={<CustomizeCountdown />} />
          {/* <Route  path={PATHS.FEATURE_REQUEST} element={FeatureRequestPage} /> */}

          <Route path={`${PATHS.WIDGETS}`}>
            <Route index element={<Widgets />} />
            <Route path={`${PATHS.WIDGETS}/:tab`} element={<Widgets />} />
          </Route>
          <Route path={PATHS.CAMPAIGNS} element={<Campaigns />} />
          <Route path={PATHS.CAMPAIGN_OVERLAPS} element={<CampaignOverlaps />} />
          <Route path={PATHS.CHECK_PRODUCT} element={<CheckProduct />} />
          {/* V3 (checkIsStoreForV4-gated fallback) removed — that check was
              hardcoded to always return true ("Forced on for every shop
              while V3 is sunset"), so the V3 branch was permanently dead. */}
          <Route path={`${PATHS.CREATE_CAMPAIGN}`} element={<ModifyCampaignV4 />} />
          <Route path={`${PATHS.EDIT_CAMPAIGN}/:campaignId`} element={<ModifyCampaignV4 />} />
          <Route path={PATHS.ANALYTICS} element={<AnalyticsPage />} />
          <Route path={PATHS.WHOLESALE_TOOLS} element={<WholesaleTools />} />
          <Route path={PATHS.SAFE_UNINSTALL} element={<SafeUninstall />} />
          <Route path={PATHS.LOGIN} element={<Login />} />
          <Route element={<NotFound />} />
        </Routes>

        {toast?.show && <ToastMessage handleDismiss={handleDismissToast} {...toast} />}
        {upgradeModalShow && (
          <Suspense fallback={null}>
            <UpgradeModal />
          </Suspense>
        )}
      </Frame>
    </Suspense>
  );
};

export default Router;
